import React from "react";
import { View, StyleSheet, Text, Platform, TouchableOpacity, Linking } from "react-native";
import { COLORS } from "../../constants";

/**
 * DetailInfoCard Component
 *
 * This component is a reusable UI card for displaying details with an icon on the left,
 * a title in the middle, and a subtitle or value on the right. The component is designed
 * to handle various use cases, including dynamic icon rendering, adjustable sizes, and
 * color customizations.
 *
 * Props:
 *  - title (string): The main title of the card, displayed in bold text.
 *  - subtitle (string): The subtitle or additional detail displayed on the right side.
 *  - FontAwesomeIcon (component): The FontAwesomeIcon component used for rendering icons.
 *  - iconName (string): The specific FontAwesome icon to display.
 *  - iconSize (number): The size of the icon.
 *  - iconColor (string): The color of the icon (default: COLORS.primary).
 *  - isLink (boolean): Indicates whether the subtitle is a clickable link.
 **/

const DetailInfoCard = ({
  title,
  subtitle,
  FontAwesomeIcon,
  iconName,
  iconSize,
  iconColor = COLORS.primary,
  isLink = false,
  showDivider = true,
}) => {
  const handlePressLink = () => {
    if (isLink && subtitle) {
      Linking.openURL(subtitle).catch((err) => console.error("Failed to open URL:", err));
    }
  };

  return (
    <View style={[styles.cardContainer, !showDivider && { borderBottomWidth: 0 }]}>
      <View style={styles.iconContainer}>
        <FontAwesomeIcon icon={iconName} size={iconSize} color={iconColor} />
      </View>
      <View style={styles.textContainer}>
        <Text style={styles.title}>{title}</Text>
        {isLink ? (
          <TouchableOpacity onPress={handlePressLink}>
            <Text style={[styles.subtitle, styles.link]}>{subtitle}</Text>
          </TouchableOpacity>
        ) : (
          <Text style={styles.subtitle}>{subtitle}</Text>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 8,
    paddingVertical: 16,
    borderBottomWidth: 0.5,
    borderColor: COLORS.border,
  },
  iconContainer: {
    width: Platform.OS === "web" ? 50 : 40,
    height: Platform.OS === "web" ? 50 : 40,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 50,
    backgroundColor: COLORS.backgroundColor,
    borderColor: COLORS.border,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 1.41,
    elevation: 2,
    marginEnd: 16,
  },
  textContainer: {
    justifyContent: "center",
  },
  title: {
    flex: 1,
    fontSize: 16,
    fontWeight: "bold",
    color: COLORS.textDark,
    lineHeight: 26,
  },
  subtitle: {
    fontSize: 16,
    fontWeight: "400",
    color: COLORS.border,
    lineHeight: 28,
  },
  link: {
    color: COLORS.primary,
    textDecorationLine: "underline",
  },
});

export default DetailInfoCard;
